import React, {useMemo} from 'react';
import {Colors, Icon, Intent, Tag, Spinner} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {Omnibar, ItemRenderer} from '@blueprintjs/select';
import debounce from 'lodash/debounce';

import {SearchResult, SearchType} from '../../models';
import {highlightText} from '../../utils/text';
import {SquareIndicator} from '../list';
import {reportIcon} from '../../utils/custom-icons';

type Callback = (value?: any) => void;

interface IMainSearchProps {
  show: boolean;
  loading: boolean;
  results: any;
  totalResults: number;
  onSearch: Callback;
  onSelect: Callback;
  onClose: Callback;
}

const SearchOmniBar = Omnibar.ofType<SearchResult>();

const renderItem: ItemRenderer<SearchResult> = (item: SearchResult, { handleClick, modifiers, query }: any) => {

  let icon: any = IconNames.CODE;
  let intent: Intent | undefined = undefined;
  let color: string | undefined = undefined;

  const itemType: string = item.type || item.itemType;
  switch (itemType) {
    case SearchType.NOTEBOOK:
      icon = IconNames.MANUAL;
      color = Colors.INDIGO3;
      break;
    case 'default': // item.type === 'default'
    case SearchType.REPORT:
      icon = reportIcon({});
      color = Colors.INDIGO3;
      break;
    case SearchType.TABLEAU_EMBED:
      icon = IconNames.DASHBOARD;
      color = Colors.INDIGO3;
      break;
  }

  return (
    <li
      key={item.uid}
      className="search-result"
      onClick={(event: any) => handleClick(event)}
    >
      <SquareIndicator
        icon={icon}
        intent={intent}
        color={color}
        width={24}
        height={24}
        iconSize={14}
      />
      <div className="search-result__item">
        <div
          className="bp4-text-overflow-ellipsis bp4-fill search-result__item__column search-result__label"
        >
          <span className="bp4-text-overflow-ellipsis">{highlightText(item.name, query)}</span>
        </div>
      </div>
    </li>
  );
};

const MainSearch = ({
  show,
  loading,
  results,
  totalResults,
  onSearch,
  onSelect,
  onClose,
}: IMainSearchProps) => {
  const debounceOnSearch = useMemo(() => debounce(onSearch, 800), [ ]);

  const handleOnItemSelect = (item: any, event: any) => {
    if (!item) {
      return;
    }

    onSelect(item);
  };

  const leftElement = (
    loading ? (
      <span className="bp4-icon">
        <Spinner size={16} className="main-search-loading" />
      </span>
    ) : <Icon icon={IconNames.SEARCH} />
  );
  const rightElement = <Tag minimal={true}>{totalResults}</Tag>

  return (
    <SearchOmniBar
      inputProps={{
        placeholder: "Search for reports...",
        leftElement,
        rightElement,
      }}
      isOpen={show}
      items={results}
      itemRenderer={renderItem}
      onItemSelect={handleOnItemSelect}
      onQueryChange={debounceOnSearch}
      onClose={onClose}
    />
  )
};

export const MainSearchComponent = React.memo(MainSearch);
